// src/pages/AdminPage.js
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeCtx } from "../App";
import { api } from "../scripts/api";
import {
  BarChart3, Upload, Table2Icon, UserCircle, DatabaseIcon,
  TruckIcon, UsersIcon, UserPlus2Icon, LucideTractor, Wrench, MapPin,
  ChevronRight, Settings2, KeyRound, Package, Boxes, ClipboardList,
  FileText, CalendarDays, Sparkles, Sun, Moon,
  Monitor, RefreshCw,
} from "lucide-react";

// ── Sidebar nav definition ────────────────────────────────────────────────────
const ADMIN_NAV = [
  {
    label: "Operations",
    color: "#3b82f6",
    items: [
      { to: "/admin/moves",           label: "Drive-Away",      icon: TruckIcon     },
      { to: "/admin/driver-calendar", label: "Driver Calendar", icon: CalendarDays  },
      { to: "/admin/driver-mgmt",     label: "Driver Mgmt",     icon: UsersIcon     },
      { to: "/admin/trip-cost",       label: "Trip Cost",        icon: MapPin        },
    ],
  },
  {
    label: "People",
    color: "#8b5cf6",
    items: [
      { to: "/admin/users",                 label: "Users",      icon: UserCircle    },
      { to: "/admin/customers/details",     label: "Customers",  icon: UserPlus2Icon },
      { to: "/admin/vendors",               label: "Vendors",    icon: LucideTractor },
    ],
  },
  {
    label: "Inventory",
    color: "#0d9488",
    items: [
      { to: "/inventory",                   label: "All Inventory",  icon: Boxes     },
      { to: "/admin/import",                label: "Import Data",    icon: Upload    },
      { to: "/admin/inventory-settings",    label: "Settings",       icon: Settings2 },
      { to: "/builder-upload",              label: "Labrie Sync",    icon: Package   },
    ],
  },
  {
    label: "PDI",
    color: "#f59e0b",
    items: [
      { to: "/pdi",           label: "Inspections", icon: ClipboardList },
      { to: "/pdi/templates", label: "Templates",   icon: FileText      },
      { to: "/pdi/reports",   label: "Reports",     icon: BarChart3     },
    ],
  },
  {
    label: "System",
    color: "#6b7280",
    items: [
      { to: "/admin/dbs",      label: "Databases", icon: DatabaseIcon },
      { to: "/admin/schema",   label: "Schema",    icon: Table2Icon   },
      { to: "/admin/api-keys", label: "API Keys",  icon: KeyRound     },
      { to: "/admin/devtools", label: "Dev Tools", icon: Wrench       },
    ],
  },
  {
    label: "Analytics",
    color: "#ec4899",
    items: [
      { to: "/reports",         label: "Reports",   icon: BarChart3       },
    ],
  },
];

const COMING_SOON = [
  { label: "Sales Brochure Generator",    sub: "AI-generated brochures for any unit"            },
  { label: "CRM Integration",             sub: "Push production & delivery updates to your CRM" },
  { label: "Social Media Integration",    sub: "Auto-post on delivery & milestones"              },
  { label: "Service Dept. Integration",   sub: "Connect IMS with your service department"        },
  { label: "Quoting Integration",         sub: "Generate quotes from inventory in one click"     },
  { label: "AI Assistant",               sub: "Natural language inventory queries & insights"    },
];

const STATUS_META = {
  available: { color: "#22c55e", label: "Available" },
  on_move:   { color: "#3b82f6", label: "On Move"   },
  off_duty:  { color: "#6b7280", label: "Off Duty"  },
};

// ── Live metrics strip ────────────────────────────────────────────────────────
function MetricsStrip() {
  const [drivers,     setDrivers]     = useState([]);
  const [activeMoves, setActiveMoves] = useState(0);
  const [loading,     setLoading]     = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, mRes] = await Promise.all([
        api.get("/driver").catch(() => ({ data: [] })),
        api.get("/moves").catch(() => ({ data: [] })),
      ]);
      setDrivers(Array.isArray(dRes.data) ? dRes.data : []);
      const moves = Array.isArray(mRes.data) ? mRes.data : [];
      setActiveMoves(moves.filter(m => !m.completed_at).length);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const available = drivers.filter(d => (d.driver_status || "available") === "available").length;
  const onMove    = drivers.filter(d => d.driver_status === "on_move").length;
  const offDuty   = drivers.filter(d => d.driver_status === "off_duty").length;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      padding: "10px 14px",
      background: "var(--card-bg)",
      border: "1px solid var(--card-border)",
      borderRadius: 12,
      marginBottom: 16,
    }}>

      <Metric
        value={activeMoves}
        label="Active Moves"
        color="#3b82f6"
        loading={loading}
      />
      <div className="tray-divider" style={{ height: 32, width: 1, background: "var(--card-border)", margin: "0 4px" }} />
      <Metric value={available} label="Available" color="#22c55e" loading={loading} />
      <Metric value={onMove}    label="On Move"   color="#3b82f6" loading={loading} />
      {offDuty > 0 && <Metric value={offDuty} label="Off Duty" color="#6b7280" loading={loading} />}

      {drivers.length > 0 && (
        <>
          <div style={{ height: 32, width: 1, background: "var(--card-border)", margin: "0 4px" }} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {drivers.map(d => {
              const s   = d.driver_status || "available";
              const col = STATUS_META[s]?.color || "#6b7280";
              return (
                <div key={d.DriverId} style={{
                  display: "flex", alignItems: "center", gap: 5,
                  fontSize: 12, padding: "3px 8px",
                  borderRadius: 99, border: `1px solid ${col}44`,
                  background: `${col}14`, color: "var(--fg)",
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: col, flexShrink: 0 }} />
                  <span style={{ fontWeight: 700 }}>{d.FullName?.split(" ")[0]}</span>
                  <span style={{ opacity: 0.6, fontSize: 10 }}>{STATUS_META[s]?.label || s}</span>
                  {d.current_location && (
                    <span style={{ opacity: 0.5, fontSize: 10 }}>· {d.current_location}</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <button
        onClick={load}
        style={{
          marginLeft: "auto", background: "none", border: "none",
          cursor: "pointer", color: "var(--muted)", padding: 4,
          display: "flex", alignItems: "center",
        }}
        title="Refresh"
      >
        <RefreshCw size={13} style={loading ? { animation: "spin 1s linear infinite" } : {}} />
      </button>
    </div>
  );
}

function Metric({ value, label, color, loading }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 6, padding: "0 6px" }}>
      <span style={{
        fontSize: 22, fontWeight: 900, lineHeight: 1,
        color: loading ? "var(--muted)" : color,
        minWidth: 24, textAlign: "center",
      }}>
        {loading ? "—" : value}
      </span>
      <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.6, whiteSpace: "nowrap", color: "var(--fg)" }}>{label}</span>
    </div>
  );
}

// ── Quick-access card ─────────────────────────────────────────────────────────
function QuickCard({ item, groupColor, onClick }) {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      style={{
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        borderRadius: 12,
        padding: "13px 14px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 11,
        transition: "transform 100ms, box-shadow 100ms, border-color 100ms",
        color: "var(--fg)",
        width: "100%",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = `0 6px 20px rgba(0,0,0,0.12)`;
        e.currentTarget.style.borderColor = groupColor + "66";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "";
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: groupColor + "18",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={15} style={{ color: groupColor }} />
      </div>
      <span style={{ fontWeight: 700, fontSize: 13 }}>{item.label}</span>
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { theme, setTheme } = useContext(ThemeCtx);

  const THEME_OPTS = [
    { value: "system", icon: Monitor, label: "System" },
    { value: "light",  icon: Sun,     label: "Light"  },
    { value: "dark",   icon: Moon,    label: "Dark"   },
  ];

  return (
    <div className="container" style={{ paddingTop: 12, paddingBottom: 56 }}>
      <div className="dash-shell">

        {/* ── Sidebar ── */}
        <aside className="dash-left">
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

            <div style={{ fontWeight: 900, fontSize: 14, letterSpacing: 0.3, padding: "2px 4px 10px", opacity: 0.9 }}>
              Admin
            </div>

            <nav className="dash-nav" style={{ flex: 1, overflowY: "auto" }}>
              {ADMIN_NAV.map((group) => (
                <div key={group.label} style={{ marginBottom: 4 }}>
                  <div className="dash-section" style={{ color: group.color, opacity: 0.85, fontWeight: 700 }}>
                    {group.label}
                  </div>
                  {group.items.map((item) => {
                    const Icon    = item.icon;
                    const isActive = loc.pathname === item.to || loc.pathname.startsWith(item.to + "/");
                    return (
                      <button
                        key={item.to}
                        className={"dash-navbtn" + (isActive ? " active" : "")}
                        onClick={() => nav(item.to)}
                        style={{
                          marginTop: 2,
                          borderColor: isActive ? group.color + "50" : undefined,
                          background:  isActive ? group.color + "12" : undefined,
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Icon size={14} style={{ color: isActive ? group.color : undefined, opacity: isActive ? 1 : 0.65, flexShrink: 0 }} />
                          <span className="dash-navtitle">{item.label}</span>
                        </span>
                        <ChevronRight size={12} style={{ opacity: 0.25 }} />
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>

            {/* Theme toggle */}
            <div style={{ borderTop: "1px solid var(--card-border)", paddingTop: 10, marginTop: 8 }}>
              <div className="dash-section" style={{ marginTop: 0 }}>Theme</div>
              <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                {THEME_OPTS.map(({ value, icon: TIcon, label }) => (
                  <button
                    key={value}
                    onClick={() => setTheme(value)}
                    title={label}
                    style={{
                      flex: 1, padding: "7px 0", borderRadius: 8, border: "1px solid",
                      borderColor: theme === value ? "var(--btn-bg)" : "var(--card-border)",
                      background:  theme === value ? "var(--btn-bg)" : "transparent",
                      color:       theme === value ? "#fff" : "var(--muted)",
                      cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <TIcon size={13} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="dash-right">

          {/* Live metrics */}
          <MetricsStrip />

          {/* Section header */}
          <div style={{ fontWeight: 900, fontSize: 20, letterSpacing: -0.3, marginBottom: 4 }}>
            Admin Tools
          </div>
          <div style={{ fontSize: 12, opacity: 0.45, marginBottom: 16 }}>
            Manage users, data, settings, and integrations
          </div>

          {/* Quick-access grid, grouped by section */}
          {ADMIN_NAV.map((group) => (
            <div key={group.label} style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: 0.5, color: group.color, opacity: 0.85,
                marginBottom: 8, display: "flex", alignItems: "center", gap: 6,
              }}>
                <div style={{ width: 3, height: 14, borderRadius: 2, background: group.color }} />
                {group.label}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
                {group.items.map((item) => (
                  <QuickCard
                    key={item.to}
                    item={item}
                    groupColor={group.color}
                    onClick={() => nav(item.to)}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Coming Soon */}
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: 0.5, opacity: 0.4, marginBottom: 10, marginTop: 8,
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <Sparkles size={12} />
            Coming Soon
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
            {COMING_SOON.map((t) => (
              <div key={t.label} style={{
                background: "var(--card-bg)",
                border: "1px solid var(--card-border)",
                borderRadius: 12,
                padding: "12px 14px",
                opacity: 0.45,
                cursor: "not-allowed",
              }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{t.label}</div>
                <div style={{ fontSize: 11, lineHeight: 1.4, opacity: 0.8 }}>{t.sub}</div>
              </div>
            ))}
          </div>

        </main>
      </div>
    </div>
  );
}
